/**
 * Retro skeleton placeholder for a search result card while ranking is in flight.
 * Mirrors the rough silhouette of <ResultCard /> (rank, artwork, title, summary,
 * meta row, action row) using neo-brutalist borders + a CRT-style sweep shimmer.
 */
interface ResultCardSkeletonProps {
  rank: number;
  delayMs?: number;
}

export default function ResultCardSkeleton({ rank, delayMs = 0 }: ResultCardSkeletonProps) {
  return (
    <div
      className="relative overflow-hidden retro-border retro-shadow bg-card p-3 sm:p-5 animate-fade-in-up"
      style={{ animationDelay: `${delayMs}ms` }}
      aria-hidden="true"
    >
      {/* CRT scan sweep */}
      <span className="crt-sweep absolute inset-0 pointer-events-none" />

      <div className="flex gap-3 sm:gap-4">
        {/* Rank badge — kept legible so the page feels structured while loading */}
        <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 retro-border bg-primary/20 flex items-center justify-center">
          <span className="font-retro font-bold text-sm sm:text-base text-primary/70">
            #{rank}
          </span>
        </div>

        {/* Square artwork placeholder */}
        <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 retro-border bg-muted skeleton-shimmer" />

        {/* Text column */}
        <div className="flex-1 min-w-0 space-y-2">
          <div className="h-3.5 sm:h-4 w-[70%] bg-muted skeleton-shimmer rounded-sm" />
          <div className="h-3 w-[45%] bg-muted/70 skeleton-shimmer rounded-sm" />
          <div className="pt-1.5 space-y-1.5">
            <div className="h-2.5 w-full bg-muted/60 skeleton-shimmer rounded-sm" />
            <div className="h-2.5 w-[92%] bg-muted/60 skeleton-shimmer rounded-sm" />
            <div className="h-2.5 w-[68%] bg-muted/60 skeleton-shimmer rounded-sm" />
          </div>
        </div>
      </div>

      {/* Meta / action row */}
      <div className="mt-4 flex items-center justify-between gap-2">
        <div className="flex gap-2">
          <div className="h-5 w-16 bg-muted/60 skeleton-shimmer rounded-sm" />
          <div className="h-5 w-12 bg-muted/60 skeleton-shimmer rounded-sm" />
        </div>
        <div className="h-7 w-20 bg-muted/70 retro-border skeleton-shimmer" />
      </div>
    </div>
  );
}
